import { useContext, createContext, PropsWithChildren } from "react";

export const LocationContext = createContext<Location | null>(null);

export const LocationProvider = (
    props: PropsWithChildren<{ location: Location | null }>
) => {
    return (
        <LocationContext.Provider value={props.location}>
            {props.children}
        </LocationContext.Provider>
    );
};

/**
 * True when running in a non-production environment where useLocation() may run
 * without a LocationProvider (Jest, Gemini VR, Storybook). Production bundles
 * inline process.env.NODE_ENV as "production", so this branch is dead code in
 * deployed builds and useLocation still throws without a provider in production.
 */
function isNonProductionEnv(): boolean {
    if (typeof process === "undefined") return false;
    const nodeEnv = process.env["NODE_ENV"];
    return nodeEnv === "test" || nodeEnv === "development";
}

/**
 * When no LocationProvider is in the tree, fall back to window.location in
 * non-production envs (Jest, Gemini VR, Storybook) so useLocalizedHref and other
 * consumers work without every test or story wrapping with LocationProvider.
 */
export const useLocation = (): Location => {
    const location = useContext(LocationContext);
    if (location) {
        return location;
    }
    if (
        isNonProductionEnv() &&
        typeof window !== "undefined" &&
        window.location
    ) {
        return window.location as Location;
    }
    throw new Error("useLocation must be used within a LocationProvider");
};
